import axios from 'axios';
import { getStoredAuthToken, shouldUseDevOwnerAuth } from '../utils/authStorage';
// Default to the website backend on 5002 so Home uses the
// same API base as auth when no REACT_APP_API_BASE is set.
const API = `${process.env.REACT_APP_API_BASE || 'http://localhost:5001'}/api`;

const extractAxiosMessage = (error) => {
  const message = error?.response?.data?.message;
  if (typeof message === 'string' && message.trim()) {
    return message;
  }

  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message;
  }

  return 'Request failed';
};

const authHeaders = () => {
  const headers = {};
  const token = getStoredAuthToken();

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  if (shouldUseDevOwnerAuth()) {
    headers['X-Tellurvision-Dev-Owner'] = 'true';
  }

  return headers;
};

export const listVideos = async (options = {}) => {
  const {
    q,
    page,
    pageSize,
    includeMeta,
    createdAfter,
    createdBefore,
    userId,
    category,
    tag,
    fundedOnly,
    sortBy,
    sortDir,
  } = options;

  const params = {};
  if (q) params.q = q;
  if (page) params.page = page;
  if (pageSize) params.page_size = pageSize;
  if (includeMeta) params.include_meta = 'true';
  if (createdAfter) params.created_after = createdAfter;
  if (createdBefore) params.created_before = createdBefore;
  if (userId) params.userId = userId;
  if (category) params.category = category;
  if (tag) params.tag = tag;
  if (fundedOnly) params.funded_only = 'true';
  if (sortBy) params.sort_by = sortBy;
  if (sortDir) params.sort_dir = sortDir;

  const { data } = await axios.get(`${API}/ads`, { params });
  return data;
};

export const createAd = async ({
  title,
  description,
  creativeType,
  imageUrl,
  videoUrl,
  landingUrl,
  channels,
  categories,
  tags,
}) => {
  const payload = {
    title,
    description,
    creativeType,
    imageUrl: imageUrl || undefined,
    videoUrl: videoUrl || undefined,
    landingUrl: landingUrl || undefined,
    channels,
    categories,
    tags,
  };

  const { data } = await axios.post(`${API}/ads`, payload, { headers: authHeaders() });
  return data;
};

export const trackAdView = async (adId) => {
  if (!adId) return;
  try {
    await axios.post(`${API}/ads/${adId}/view`);
  } catch {
    // tracking is best-effort; ignore errors in UI
  }
};

export const trackAdClick = async (adId) => {
  if (!adId) return;
  try {
    await axios.post(`${API}/ads/${adId}/click`);
  } catch {
    // best-effort
  }
};

export const getAd = async (id) => {
  if (!id) {
    throw new Error('Missing ad id');
  }
  const { data } = await axios.get(`${API}/ads/${id}`, { headers: authHeaders() });
  return data;
};

export const assignAdToUser = async (adId, userId) => {
  if (!adId) {
    throw new Error('Missing ad id');
  }
  if (!userId) {
    throw new Error('Missing user id');
  }

  try {
    const { data } = await axios.post(
      `${API}/ads/${adId}/assign`,
      { userId },
      { headers: authHeaders() },
    );

    return data;
  } catch (error) {
    throw new Error(extractAxiosMessage(error));
  }
};

export const bulkAssignAdsToUser = async ({ adIds, userId }) => {
  if (!Array.isArray(adIds) || adIds.length === 0) {
    throw new Error('Missing ad ids');
  }
  if (!userId) {
    throw new Error('Missing user id');
  }

  try {
    const { data } = await axios.post(
      `${API}/ads/bulk/assign`,
      { adIds, userId },
      { headers: authHeaders() },
    );

    return data;
  } catch (error) {
    throw new Error(extractAxiosMessage(error));
  }
};
